import React, { useEffect, useState, useCallback } from 'react';
import { FaPlus, FaEdit, FaTrash } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { getApiEndpoint } from '../../../config/api';
import { getAuthHeader } from '../../../utils/auth';
import './QualificationsAdmin.css';

const CATEGORIES = ['general', 'safety', 'electrical', 'roof', 'lifts'];

const QualificationsAdmin = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: '', category: 'general', description: '' });

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(getApiEndpoint('/api/qualifications'), { headers: getAuthHeader() });
      if (!r.ok) throw new Error();
      const data = await r.json();
      setItems(Array.isArray(data?.qualifications) ? data.qualifications : []);
    } catch (_) { toast.error('Could not load qualifications'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  const startCreate = () => {
    setEditing(null);
    setForm({ name: '', category: 'general', description: '' });
    setShowForm(true);
  };

  const startEdit = (q) => {
    setEditing(q);
    setForm({ name: q.name, category: q.category || 'general', description: q.description || '' });
    setShowForm(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return toast.error('Name is required');
    try {
      const url = editing ? `/api/qualifications/${editing.id}` : '/api/qualifications';
      const r = await fetch(getApiEndpoint(url), {
        method: editing ? 'PUT' : 'POST',
        headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (!r.ok && r.status !== 202) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.message || 'Failed');
      }
      toast.success(editing ? 'Updated' : 'Created');
      setShowForm(false);
      fetchItems();
    } catch (e) { toast.error(e.message); }
  };

  const remove = async (q) => {
    if (!window.confirm(`Delete "${q.name}"? Existing user assignments stay but no new ones can be added.`)) return;
    try {
      const r = await fetch(getApiEndpoint(`/api/qualifications/${q.id}`), { method: 'DELETE', headers: getAuthHeader() });
      if (!r.ok) throw new Error();
      toast.success('Deleted');
      fetchItems();
    } catch (_) { toast.error('Failed to delete'); }
  };

  const grouped = items.reduce((acc, q) => {
    const cat = q.category || 'general';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(q);
    return acc;
  }, {});

  return (
    <div className="cs-qa-page">
      <div className="cs-qa-header">
        <div>
          <h1>Qualifications</h1>
          <p className="cs-qa-sub">Manage the qualifications taxonomy used across CloudSafe — the gate for quote submissions and job assignment.</p>
        </div>
        <button type="button" className="cs-qa-add" onClick={startCreate}>
          <FaPlus /> New qualification
        </button>
      </div>

      {loading ? (
        <div className="cs-qa-state">Loading…</div>
      ) : items.length === 0 ? (
        <div className="cs-qa-state">No qualifications configured yet. Add your first one to enable the quote gate for FM jobs.</div>
      ) : (
        <div className="cs-qa-groups">
          {Object.entries(grouped).map(([cat, list]) => (
            <section key={cat} className="cs-qa-group">
              <h2 className="cs-qa-group-title">{cat}</h2>
              <ul className="cs-qa-list">
                {list.map(q => (
                  <li key={q.id} className="cs-qa-row">
                    <div>
                      <strong className="cs-qa-name">{q.name}</strong>
                      {q.description && <span className="cs-qa-desc"> — {q.description}</span>}
                    </div>
                    <div className="cs-qa-actions">
                      <button type="button" onClick={() => startEdit(q)} title="Edit"><FaEdit /></button>
                      <button type="button" onClick={() => remove(q)} title="Delete"><FaTrash /></button>
                    </div>
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </div>
      )}

      {showForm && (
        <div className="cs-qa-overlay" onClick={() => setShowForm(false)}>
          <form className="cs-qa-modal" onSubmit={submit} onClick={e => e.stopPropagation()}>
            <h3>{editing ? 'Edit qualification' : 'New qualification'}</h3>
            <label>
              Name
              <input
                type="text"
                value={form.name}
                onChange={e => setForm({ ...form, name: e.target.value })}
                required
                placeholder="e.g. IRATA Level 2"
              />
            </label>
            <label>
              Category
              <select value={form.category} onChange={e => setForm({ ...form, category: e.target.value })}>
                {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </label>
            <label>
              Description (optional)
              <textarea
                value={form.description}
                onChange={e => setForm({ ...form, description: e.target.value })}
                rows={3}
              />
            </label>
            <div className="cs-qa-form-actions">
              <button type="button" onClick={() => setShowForm(false)}>Cancel</button>
              <button type="submit" className="primary">{editing ? 'Save' : 'Create'}</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default QualificationsAdmin;
